import PropTypes from 'prop-types'
import normalizeQuestionMark from './normalizeQuestionMark'

/**
 * Converts a string, including handling Nordic characters and question marks, to camel case.
 * Ensures the first character is lowercase unless it's a single word input like "Øl".
 *
 * @param {string} str - The string to convert.
 * @returns {string} The camel case version of the input string.
 */
const toCamelCase = (str) => {
  if (!str || typeof str !== 'string') {
    return ''
  }
  // Replace question marks with "Question Mark"
  const strWithQuestionMarkHandled = normalizeQuestionMark(str)
  const replaceChars = (str) => {
    const replacements = { æ: 'ae', ø: 'o', å: 'a' }
    return str?.replace(
      /[æøåÆØÅ]/g,
      (match) => replacements[match.toLowerCase()] || match
    )
  }

  const replacedStr = replaceChars(strWithQuestionMarkHandled)

  // Remove any non-word or space characters (except underscores and dashes)
  const cleanedStr = replacedStr.replace(/[^\p{L}\p{N}\s_-]/gu, '').trimStart()

  const result = cleanedStr
    ?.split(/[-_\s]+/)
    ?.map((word, index) =>
      index === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('')

  // Handle special case where input is a single word that starts with a Nordic character
  if (
    cleanedStr?.split(/[-_\s]+/).length === 1 &&
    /[ÆØÅæøå]/.test(str.charAt(0))
  ) {
    return result.charAt(0).toLowerCase() + result.slice(1)
  }

  return result
}

toCamelCase.propTypes = {
  str: PropTypes.string.isRequired
}

export default toCamelCase
